'use client';

import { 
  ControlType, 
  CalculatorId, 
  CalculatorType, 
  ControlIds, 
  type CalculatorConfig, 
  type ControlValues,
  type MultiColumnSelectControl,
  type TextControl,
  type SelectControl,
  type NumberControl
} from '../../../lib/calculators/calculator-config';
import { TableDataJSON } from '../../../src/types/table-json';
import { LazyTabLabECharts } from '@/components/ui/charts/lazy-tab-lab-echarts';
import type { EChartsOption } from 'echarts';

// Type for heatmap specific control values
export interface HeatmapControlValues extends ControlValues {
  [ControlIds.Values]: string[];
  [ControlIds.Title]: string;
  [ControlIds.ColorScheme]: 'sequential' | 'diverging';
}

// Define the controls for the heatmap
export const heatmapControls = {
  tabs: [
    {
      title: 'Data',
      controls: [
        {
          id: ControlIds.Values,
          type: ControlType.MultiColumnSelect,
          label: 'Data columns',
          required: true,
          numericOnly: true,
          minSelections: 2
        } satisfies MultiColumnSelectControl,
        {
          id: ControlIds.Title,
          type: ControlType.Text,
          label: 'Chart title',
          required: false
        } satisfies TextControl
      ]
    },
    {
      title: 'Appearance',
      controls: [
        {
          id: ControlIds.ColorScheme,
          type: ControlType.Select,
          label: 'Color scheme',
          options: [
            { label: 'Sequential', value: 'sequential' },
            { label: 'Diverging', value: 'diverging' }
          ],
          defaultValue: 'sequential'
        } satisfies SelectControl
      ]
    }
  ]
};

// Pure function to generate ECharts config for a heatmap
export function getHeatmapEChartsConfig({
  data,
  xLabels,
  yLabels,
  title,
  colorScheme
}: {
  data: number[][];
  xLabels: string[];
  yLabels: string[];
  title?: string;
  colorScheme: 'sequential' | 'diverging';
}): EChartsOption {
  // Transform data into the format expected by ECharts
  const seriesData = data.flatMap((row, i) => 
    row.map((value, j) => [j, i, value])
  );

  // Calculate min and max for color scale
  const values = data.flat();
  const min = Math.min(...values);
  const max = Math.max(...values);

  // Define color schemes
  const colors = colorScheme === 'sequential' 
    ? ['#fff', '#5470c6'] 
    : ['#2c7bb6', '#fff', '#d7191c'];

  return {
    title: {
      text: title || 'Heatmap',
      left: 'center',
      top: 2,
      padding: [0, 0, 0, 0]
    },
    tooltip: {
      position: 'top',
      formatter: (params: any) => {
        const value = params.data[2];
        return `${xLabels[params.data[0]]} - ${yLabels[params.data[1]]}<br/>Value: ${value.toFixed(2)}`;
      }
    },
    grid: {
      left: '5%',
      right: '5%',
      bottom: '5%',
      top: '10%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: xLabels,
      splitArea: {
        show: true
      }
    },
    yAxis: {
      type: 'category',
      data: yLabels,
      splitArea: {
        show: true
      }
    },
    visualMap: {
      min,
      max,
      calculable: true,
      orient: 'horizontal',
      left: 'center',
      bottom: '0%',
      inRange: {
        color: colors
      }
    },
    series: [
      {
        name: 'Heatmap',
        type: 'heatmap',
        data: seriesData,
        label: {
          show: true,
          formatter: (params: any) => params.data[2].toFixed(1)
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        }
      }
    ]
  };
}

export const heatmapConfig: CalculatorConfig<CalculatorId.Heatmap> = {
  id: CalculatorId.Heatmap,
  type: CalculatorType.Graph,
  name: 'Heatmap',
  initialData: TableDataJSON.from({
    columns: [
      { 
        id: 'monday', 
        name: 'Monday', 
        values: ['10', '15', '20', '25', '30'] 
      },
      { 
        id: 'tuesday', 
        name: 'Tuesday', 
        values: ['12', '18', '22', '28', '32'] 
      },
      { 
        id: 'wednesday', 
        name: 'Wednesday', 
        values: ['15', '20', '25', '30', '35'] 
      },
      { 
        id: 'thursday', 
        name: 'Thursday', 
        values: ['18', '22', '28', '32', '38'] 
      },
      { 
        id: 'friday', 
        name: 'Friday', 
        values: ['20', '25', '30', '35', '40'] 
      }
    ]
  }),
  initialControlValues: {
    [ControlIds.Values]: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    [ControlIds.Title]: 'Weekly Activity Heatmap',
    [ControlIds.ColorScheme]: 'sequential'
  },
  controls: heatmapControls,
  getInitialConfig: (data: TableDataJSON) => {
    const numericColumns = data.columns.filter(col => 
      col.values.every(val => !isNaN(Number(val)))
    );

    return {
      [ControlIds.Values]: numericColumns.map(col => col.id),
      [ControlIds.Title]: 'Heatmap',
      [ControlIds.ColorScheme]: 'sequential'
    };
  },
  getVisualizationData: ({ data, controls }) => {
    const heatmapControls = controls as HeatmapControlValues;
    
    if (!data?.columns?.length || !heatmapControls[ControlIds.Values]?.length) {
      return null;
    }

    const selectedColumns = heatmapControls[ControlIds.Values]
      .map(id => data.columns.find(col => col.id === id))
      .filter((col): col is typeof data.columns[0] => col !== undefined);

    if (selectedColumns.length < 2) {
      return null;
    }

    // Make sure we have at least one column with values
    if (!selectedColumns[0]?.values?.length) {
      return null;
    }

    // Create the matrix data
    const matrixData = selectedColumns[0].values.map((_, rowIndex) =>
      selectedColumns.map(col => Number(col.values[rowIndex]))
    );

    return getHeatmapEChartsConfig({
      data: matrixData,
      xLabels: selectedColumns.map(col => col.name),
      yLabels: selectedColumns[0].values.map((_, i) => `Row ${i + 1}`),
      title: heatmapControls[ControlIds.Title],
      colorScheme: heatmapControls[ControlIds.ColorScheme]
    });
  },
  render: ({ visualizationData }) => 
    visualizationData ? (
      <LazyTabLabECharts option={visualizationData} className="h-full w-full" />
    ) : null
}; 